import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Plus, Save, Trash2, Loader2, Search, ExternalLink, Car, User, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "../../lib/api";
import { openWhatsApp, waTemplates } from "../../lib/whatsapp";

// ---------- LIST ---------------------------------------------------
export const AdminClients = () => {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const data = await adminApi.listClients(q ? { query: q } : {});
      setRows(Array.isArray(data) ? data : []);
    } catch { toast.error("Could not load clients"); }
    finally { setLoading(false); }
  };
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q]);

  return (
    <div data-testid="admin-clients">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="font-display uppercase tracking-widest text-xs text-[#f28a16]">End-user directory</div>
          <h1 className="font-display font-black uppercase text-3xl sm:text-4xl text-[#111619] mt-1">Clients &amp; vehicles</h1>
          <p className="text-[#3f4b52] mt-1 text-sm max-w-2xl">
            The actual vehicle owners — search by name, mobile, <b>registration</b>, or <b>VIN</b>. One client can own many vehicles.
          </p>
        </div>
        <Link
          to="/admin/clients/new"
          data-testid="new-client-btn"
          className="bg-[#f28a16] text-white font-display uppercase tracking-widest text-xs px-5 h-11 rounded-md inline-flex items-center gap-2 hover:brightness-110"
        >
          <Plus size={14} /> New client
        </Link>
      </div>

      <div className="mt-6">
        <div className="relative max-w-lg">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9aa6ad]" />
          <input
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name · mobile · reg no · VIN…"
            className="w-full pl-9 pr-3 h-10 border border-[#cfd7db] rounded-md text-sm"
            data-testid="clients-search"
          />
        </div>
      </div>

      <div className="mt-6 bg-white border border-[#cfd7db] rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#111619] text-white text-left">
            <tr>
              <Th>Client</Th><Th>Contact</Th><Th>Vehicles</Th><Th></Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#eef2f4]">
            {loading && <tr><td colSpan={4} className="p-6 text-center text-[#3f4b52]">Loading…</td></tr>}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={4} className="p-8 text-center text-[#3f4b52]" data-testid="clients-empty">
                No clients yet — click <b>New client</b> to add one.
              </td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.id} className="hover:bg-[#f5f6f6]" data-testid={`client-row-${r.id}`}>
                <Td className="font-bold text-[#111619]">
                  <User size={12} className="inline mr-1 text-[#f28a16]" />
                  {r.full_name || r.first_name}
                </Td>
                <Td className="text-[#3f4b52]">{[r.mobile, r.email].filter(Boolean).join(" · ") || "—"}</Td>
                <Td>
                  {(r.vehicles || []).length === 0 ? (
                    <span className="text-[#9aa6ad] text-xs">no vehicles</span>
                  ) : (
                    <div className="flex flex-wrap gap-1">
                      {(r.vehicles || []).slice(0, 4).map((v) => (
                        <span key={v.id} className="inline-flex items-center gap-1 border border-[#cfd7db] px-2 py-0.5 rounded-sm text-xs">
                          <Car size={11} className="text-[#f28a16]" />
                          <b className="font-mono">{v.registration}</b>
                          {v.make_model && <span className="text-[#3f4b52]">· {v.make_model}</span>}
                        </span>
                      ))}
                      {(r.vehicles || []).length > 4 && (
                        <span className="text-xs text-[#3f4b52]">+{r.vehicles.length - 4} more</span>
                      )}
                    </div>
                  )}
                </Td>
                <Td className="text-right">
                  {r.mobile && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        openWhatsApp(r.mobile,
                          `Hi ${(r.first_name || "").trim() || "there"}, ${waTemplates.documents_ready({ client_name: r.full_name, job_number: "—", portal_url: "" })
                            .replace(/^Hi[^,]+,\s*/, "")}`.trim() ||
                          `Hi ${(r.first_name || "").trim() || "there"}, this is Diesel & Dust Windscreens.`);
                      }}
                      className="text-[#25D366] hover:brightness-110 inline-flex items-center gap-1 text-xs mr-3"
                      data-testid={`whatsapp-client-${r.id}`}
                      title="Open WhatsApp with this client"
                    >
                      <MessageCircle size={13} /> WhatsApp
                    </button>
                  )}
                  <Link to={`/admin/clients/${r.id}`} className="text-[#f28a16] hover:underline inline-flex items-center gap-1 text-xs" data-testid={`open-client-${r.id}`}>
                    Open <ExternalLink size={11} />
                  </Link>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ---------- CREATE / EDIT ------------------------------------------
const blankVehicle = { registration: "", vin: "", make_model: "", year: "", colour: "" };
const blankClient = { first_name: "", last_name: "", mobile: "", email: "", telephone: "", id_number: "", address: "", notes: "", vehicles: [blankVehicle] };

export const AdminClientForm = () => {
  const { id } = useParams();
  const nav = useNavigate();
  const editing = !!id;
  const [form, setForm] = useState(blankClient);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!editing) return;
    (async () => {
      try {
        const c = await adminApi.getClient(id);
        setForm({ ...blankClient, ...c, vehicles: (c.vehicles || []).length ? c.vehicles : [blankVehicle] });
      } catch { toast.error("Could not load client"); }
    })();
  }, [id, editing]);

  const set = (k) => (e) => setForm({ ...form, [k]: e?.target?.value ?? e });

  const setVehicle = (idx, patch) => {
    setForm((f) => {
      const vs = [...f.vehicles];
      vs[idx] = { ...vs[idx], ...patch };
      return { ...f, vehicles: vs };
    });
  };
  const addVehicle = () => setForm((f) => ({ ...f, vehicles: [...f.vehicles, { ...blankVehicle }] }));
  const rmVehicle = async (idx) => {
    const v = form.vehicles[idx];
    if (editing && v.id) {
      if (!window.confirm(`Remove ${v.registration || "this vehicle"} from this client?`)) return;
      try {
        await adminApi.removeClientVehicle(id, v.id);
        setForm((f) => ({ ...f, vehicles: f.vehicles.filter((_, i) => i !== idx) }));
        toast.success("Vehicle removed");
      } catch (err) { toast.error(err?.response?.data?.detail || "Could not remove vehicle"); }
    } else {
      setForm((f) => ({ ...f, vehicles: f.vehicles.filter((_, i) => i !== idx) }));
    }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim()) return toast.error("First name is required");
    // strip blank vehicles
    const cleanVehicles = (form.vehicles || []).filter((v) => (v.registration || "").trim());
    // Coerce blank optional fields to null so Pydantic EmailStr / EmailStr(None) accepts them
    const nullIfBlank = (v) => (v === "" ? null : v);
    const cleanBody = {
      ...form,
      email: nullIfBlank(form.email),
      mobile: nullIfBlank(form.mobile),
      telephone: nullIfBlank(form.telephone),
      id_number: nullIfBlank(form.id_number),
      last_name: nullIfBlank(form.last_name),
      address: nullIfBlank(form.address),
      notes: nullIfBlank(form.notes),
    };
    setBusy(true);
    try {
      let saved;
      if (editing) {
        // Save client details first
        const { vehicles, ...rest } = cleanBody;
        saved = await adminApi.updateClient(id, rest);
        // Then sync vehicles (add/update; the list-level save doesn't touch vehicles server-side)
        for (const v of cleanVehicles) {
          if (v.id) {
            await adminApi.updateClientVehicle(id, v.id, v);
          } else {
            await adminApi.addClientVehicle(id, v);
          }
        }
        saved = await adminApi.getClient(id);
      } else {
        saved = await adminApi.createClient({ ...cleanBody, vehicles: cleanVehicles });
      }
      toast.success(editing ? "Client updated" : "Client created");
      nav(`/admin/clients/${saved.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save client");
    } finally { setBusy(false); }
  };

  const removeClient = async () => {
    if (!window.confirm("Delete this client and all their vehicles?")) return;
    setBusy(true);
    try { await adminApi.deleteClient(id); toast.success("Client deleted"); nav("/admin/clients"); }
    catch (err) { toast.error(err?.response?.data?.detail || "Could not delete"); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={save} data-testid="admin-client-form">
      <Link to="/admin/clients" className="inline-flex items-center gap-2 text-xs font-display uppercase tracking-widest text-[#3f4b52] hover:text-[#f28a16]">
        <ArrowLeft size={14} /> Back to clients
      </Link>
      <div className="mt-3 flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="font-display uppercase tracking-widest text-xs text-[#f28a16]">Client (end-user)</div>
          <h1 className="font-display font-black uppercase text-3xl sm:text-4xl text-[#111619] mt-1">
            {editing ? (form.full_name || `${form.first_name} ${form.last_name}`) : "New client"}
          </h1>
        </div>
        <div className="flex gap-2">
          {editing && (
            <button type="button" onClick={removeClient} disabled={busy} data-testid="client-delete-btn"
              className="border border-[#dc2626] text-[#dc2626] font-display uppercase tracking-widest text-xs px-4 h-10 rounded-md inline-flex items-center gap-2 hover:bg-[#fef2f2]">
              <Trash2 size={13} /> Delete
            </button>
          )}
          <button type="submit" disabled={busy} data-testid="client-save-btn"
            className="bg-[#f28a16] text-white font-display uppercase tracking-widest text-xs px-5 h-10 rounded-md inline-flex items-center gap-2 hover:brightness-110">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            {editing ? "Save changes" : "Create client"}
          </button>
        </div>
      </div>

      {/* Personal details */}
      <div className="mt-6 bg-white border border-[#cfd7db] rounded-md p-5 grid grid-cols-1 md:grid-cols-2 gap-4 max-w-5xl">
        <F label="First name *"><Inp val={form.first_name} on={set("first_name")} testid="client-first-name" /></F>
        <F label="Last name"><Inp val={form.last_name} on={set("last_name")} testid="client-last-name" /></F>
        <F label="Mobile"><Inp val={form.mobile} on={set("mobile")} testid="client-mobile" /></F>
        <F label="Email"><Inp type="email" val={form.email} on={set("email")} testid="client-email" /></F>
        <F label="Telephone"><Inp val={form.telephone} on={set("telephone")} /></F>
        <F label="ID number"><Inp val={form.id_number} on={set("id_number")} /></F>
        <F label="Address" span={2}><textarea value={form.address} onChange={set("address")} rows={2} className="w-full px-3 py-2 border border-[#cfd7db] rounded-md text-sm" /></F>
        <F label="Notes" span={2}><textarea value={form.notes} onChange={set("notes")} rows={2} className="w-full px-3 py-2 border border-[#cfd7db] rounded-md text-sm" /></F>
      </div>

      {/* Vehicles */}
      <div className="mt-6 bg-white border border-[#cfd7db] rounded-md overflow-hidden max-w-5xl">
        <div className="p-4 border-b border-[#cfd7db] flex items-center justify-between">
          <div>
            <div className="font-display uppercase tracking-widest text-xs text-[#f28a16]">Vehicles</div>
            <p className="text-xs text-[#3f4b52] mt-1">One client can own many vehicles. Reg number is unique across the whole app.</p>
          </div>
          <button type="button" onClick={addVehicle} data-testid="add-vehicle-btn"
            className="text-xs text-[#f28a16] hover:underline inline-flex items-center gap-1">
            <Plus size={12} /> Add vehicle
          </button>
        </div>
        <div className="p-4 space-y-3">
          {form.vehicles.map((v, i) => (
            <div key={i} className="border border-[#e5e7eb] rounded-md p-3 grid grid-cols-1 md:grid-cols-5 gap-3" data-testid={`vehicle-${i}`}>
              <F label="Reg no. *"><Inp val={v.registration} on={(e) => setVehicle(i, { registration: (e?.target?.value ?? e).toUpperCase() })} testid={`vehicle-${i}-reg`} /></F>
              <F label="VIN"><Inp val={v.vin} on={(e) => setVehicle(i, { vin: (e?.target?.value ?? e).toUpperCase() })} testid={`vehicle-${i}-vin`} /></F>
              <F label="Make / Model"><Inp val={v.make_model} on={(e) => setVehicle(i, { make_model: e?.target?.value ?? e })} testid={`vehicle-${i}-make`} /></F>
              <F label="Year"><Inp val={v.year} on={(e) => setVehicle(i, { year: e?.target?.value ?? e })} /></F>
              <div className="flex items-end justify-between gap-2">
                <F label="Colour"><Inp val={v.colour} on={(e) => setVehicle(i, { colour: e?.target?.value ?? e })} /></F>
                <button type="button" onClick={() => rmVehicle(i)}
                  className="text-[#dc2626] hover:brightness-110 h-10 px-2" data-testid={`rm-vehicle-${i}`} aria-label="Remove vehicle">
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Fitment history — persistent per reg number even after ownership change */}
      {editing && form.vehicles.some((v) => v.registration) && (
        <VehicleHistorySection vehicles={form.vehicles.filter((v) => v.registration)} />
      )}
    </form>
  );
};

// ---------- Vehicle history ---------------------------------------
const VehicleHistorySection = ({ vehicles }) => {
  const [byReg, setByReg] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const out = {};
      for (const v of vehicles) {
        try {
          const rows = await adminApi.vehicleHistory(v.registration);
          out[v.registration] = rows;
        } catch { out[v.registration] = []; }
      }
      if (!cancelled) { setByReg(out); setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [vehicles.length]); // eslint-disable-line

  return (
    <div className="mt-6 bg-white border border-[#cfd7db] rounded-md overflow-hidden max-w-5xl" data-testid="vehicle-history-section">
      <div className="p-4 border-b border-[#cfd7db]">
        <div className="font-display uppercase tracking-widest text-xs text-[#f28a16]">Fitment history</div>
        <p className="text-xs text-[#3f4b52] mt-1">
          Every job ever done on these registration numbers — including under previous owners.
        </p>
      </div>
      <div className="p-4 space-y-4">
        {loading && <div className="text-xs text-[#3f4b52]" data-testid="vh-loading">Loading history…</div>}
        {!loading && vehicles.map((v) => {
          const rows = byReg[v.registration] || [];
          return (
            <div key={v.id || v.registration}>
              <div className="text-xs font-display uppercase tracking-wider font-bold text-[#111619] mb-2 inline-flex items-center gap-2">
                <Car size={13} className="text-[#f28a16]" />
                <span className="font-mono">{v.registration}</span>
                {v.make_model && <span className="text-[#3f4b52] font-normal normal-case tracking-normal">· {v.make_model}</span>}
                <span className="text-[#3f4b52] font-normal normal-case tracking-normal">({rows.length} job{rows.length === 1 ? "" : "s"})</span>
              </div>
              {rows.length === 0 ? (
                <div className="text-xs text-[#3f4b52] italic" data-testid={`vh-empty-${v.registration}`}>No prior fitments.</div>
              ) : (
                <table className="w-full text-xs border border-[#eef2f4] rounded-md" data-testid={`vh-table-${v.registration}`}>
                  <thead className="bg-[#f5f6f6] text-[#3f4b52]">
                    <tr>
                      <th className="px-2 py-1.5 text-left font-display uppercase tracking-wider">Job</th>
                      <th className="px-2 py-1.5 text-left font-display uppercase tracking-wider">Date</th>
                      <th className="px-2 py-1.5 text-left font-display uppercase tracking-wider">Owner</th>
                      <th className="px-2 py-1.5 text-left font-display uppercase tracking-wider">Insurer</th>
                      <th className="px-2 py-1.5 text-left font-display uppercase tracking-wider">Status</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#eef2f4]">
                    {rows.map((j) => (
                      <tr key={j.id}>
                        <td className="px-2 py-1.5 font-mono">#{j.job_number}</td>
                        <td className="px-2 py-1.5">{(j.fitment_date || "").slice(0, 10) || "—"}</td>
                        <td className="px-2 py-1.5">{j.client_name || "—"}</td>
                        <td className="px-2 py-1.5">{j.insurance_company || "—"}</td>
                        <td className="px-2 py-1.5"><span className="text-[#3f4b52]">{j.status}</span></td>
                        <td className="px-2 py-1.5 text-right">
                          <Link to={`/admin/jobs/${j.id}`} className="text-[#f28a16] hover:underline inline-flex items-center gap-1">
                            Open <ExternalLink size={10} />
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ---------- shared bits -------------------------------------------
const Th = ({ children }) => <th className="px-3 py-2 font-display uppercase tracking-widest text-xs">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-3 py-2.5 ${className}`}>{children}</td>;
const F = ({ label, children, span = 1 }) => (
  <div className={span === 2 ? "md:col-span-2" : ""}>
    <label className="text-xs font-display uppercase tracking-wider font-bold text-[#3f4b52] block mb-1">{label}</label>
    {children}
  </div>
);
const Inp = ({ val, on, type = "text", testid }) => (
  <input type={type} value={val ?? ""} onChange={on} data-testid={testid}
    className="w-full h-10 px-3 border border-[#cfd7db] rounded-md text-sm" />
);

export default AdminClients;
